import React, { useMemo } from 'react';
import { ThemeProvider } from '@unisporkal/themes';
import { LocalizationProvider } from '@unisporkal/localization';
import { lazyProps } from '@unisporkal/react-lazy-component';
import { useQueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { UserProvider } from '@unisporkal/user';
import { useSite } from '@unisporkal/sites';
import { useExperience } from '@unisporkal/experiences';
import { AttrTrackingProvider } from '@unisporkal/attr-tracking/react';
import queryKeys from '../../../support/uiStateKeys';
import AssetView from '../../../views/assetView';
import { type AssetView as AssetViewType } from '../../../types/assetView.types';
import { AssetAcquisitionModalProvider } from '../../../contexts/AssetAcquisitionModalContext';
import { AssetProvider } from '../../providers/AssetProvider';
import useDownloadAgreements from '../../api/download/useDownloadAgreements';
import useUserQuery from '../../api/users/useUserQuery';
import useAssetLicensed from '../../api/license/useAssetLicensed';
import { useSubscriptionInfo, useRfSubscriptionStatus } from '../../api';
import { ModifiedDownloadProvider } from '../../../contexts/ModifiedDownloadContext';
import { getAttrTrackingMap } from '../../../config/tracking';

const Translations = lazyProps(
  (props: { site: string; locale: string }) =>
    import(`locales/${props.site}/${props.locale}.yml`)
);

interface ProviderProps {
  assets: unknown[];
  children: React.ReactNode;
  dropboxApp?: string | null;
  isAcquisitionModalOpen?: boolean;
  onCloseModal?: (() => void) | null;
  mode: string;
  hideALaCarte?: boolean;
  genId?: string | null;
  index?: number | null;
  hasTransparency?: boolean;
  onModifiedDownloadSuccess?: () => void;
}

const Providers = ({
  assets,
  children,
  dropboxApp = null,
  isAcquisitionModalOpen = false,
  onCloseModal = null,
  mode,
  hideALaCarte = false,
  genId = null,
  index = null,
  hasTransparency = false,
  onModifiedDownloadSuccess = () => {},
}: ProviderProps) => {
  const site = useSite();
  const siteName = site.nickname;
  const queryClient = useQueryClient();
  const attrTrackingMap = getAttrTrackingMap(
    useExperience('GA4DecouplingAssetAcquisition').isActive()
  );
  const { response: user } = useUserQuery();
  const locale = user?.locale || 'en-us';
  const localAssets = useMemo<AssetViewType[]>(
    () => assets.map((localAsset) => new AssetView(localAsset)),
    [assets]
  );
  const { response: assetLicense } = useAssetLicensed({
    asset: localAssets[0],
    isBulkDownload: localAssets.length > 1,
  });

  const isAuthenticated = user?.isAuthenticated ?? false;
  const isRemembered = user?.isRemembered ?? false;

  const { response: downloadAgreements } = useDownloadAgreements({
    assetId: localAssets[0].id,
    assets: localAssets,
    user,
  });

  useRfSubscriptionStatus({
    isAuthenticated,
    isRemembered,
    assetId: localAssets[0]?.id,
  });

  useSubscriptionInfo({
    family: localAssets[0]?.family,
    isAuthenticated,
    isRemembered,
  });

  queryClient.setQueryData([queryKeys.MODE], mode);
  queryClient.setQueryData([queryKeys.DROPBOX_APP], dropboxApp);

  return (
    <AssetProvider assets={localAssets}>
      <UserProvider user={user}>
        <ThemeProvider theme={siteName}>
          <Translations
            site={siteName}
            locale={locale}
          >
            {(translations: Record<string, string>) => (
              <LocalizationProvider
                translations={translations}
                locale={locale}
              >
                <AssetAcquisitionModalProvider
                  isAcquisitionModalOpen={isAcquisitionModalOpen}
                  onCloseModal={onCloseModal}
                  mode={mode}
                >
                  <ModifiedDownloadProvider
                    genId={genId}
                    index={index}
                    onModifiedDownloadSuccess={onModifiedDownloadSuccess}
                    hasTransparency={hasTransparency}
                    assetLicense={assetLicense?.largestLicense}
                    downloadAgreements={downloadAgreements}
                    hideALaCarte={hideALaCarte}
                  >
                    {/* No ancestor renders markup, so `as` has nothing to adopt; this div deliberately excludes the sibling ReactQueryDevtools overlay above. */}
                    <AttrTrackingProvider map={attrTrackingMap}>
                      {children}
                    </AttrTrackingProvider>
                  </ModifiedDownloadProvider>
                </AssetAcquisitionModalProvider>
                <ReactQueryDevtools initialIsOpen={false} />
              </LocalizationProvider>
            )}
          </Translations>
        </ThemeProvider>
      </UserProvider>
    </AssetProvider>
  );
};

export default Providers;
